import { Component, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { ChildrenSelectorComponent, Student } from '../../shared/children-selector/children-selector.component';
import { PageTitleComponent } from '../../shared/page-title/page-title.component';
import { MessagerieService, MessageTarget, Conversation, AddMessageRequest } from '../../services/messagerie.service';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { LoaderComponent } from "../../shared/loader/loader.component";

interface GroupedMessages {
  date: string;
  messages: any[];
}

@Component({
  selector: 'app-messagerie',
  standalone: true,
  imports: [CommonModule, FormsModule, ChildrenSelectorComponent, PageTitleComponent, TranslatePipe, LoaderComponent],
  templateUrl: './messagerie.component.html',
  styleUrl: './messagerie.component.css'
})
export class MessagerieComponent {
  @ViewChild('messagesContainer') private messagesContainerRef!: ElementRef;
  selectedStudent: Student | null = null;
  conversations: Conversation[] = [];
  messageTargets: MessageTarget[] = [];
  selectedConversation: Conversation | null = null;
  currentConversation: Conversation | null = null;
  groupedMessages: GroupedMessages[] = []; // New property to hold grouped messages
  newMessage: string = '';
  selectedFiles: File[] = [];
  isLoadingConversations: boolean = false;
  isLoadingConversation: boolean = false;
  isLoadingMessages: boolean = false;
  isSendingMessage: boolean = false;
  isConnected: boolean = true;
  unreadCount: number = 0;
  showTargetsModal: boolean = false;
  isLoadingTargets: boolean = false;
  isLoadingTeachers: boolean = false;
  currentConversationId: number = 0;
  currentFriendId: number = 0;
  private subscriptions: Subscription[] = [];
  private pollingSubscription: Subscription | null = null;
  private isPollingActive: boolean = false;
  showFileModal: boolean = false;
  currentFile: any = null;

  // Mock organization ID - you should get this from your auth service or config
  private organizationId: number = 1;

  constructor(
    private messagerieService: MessagerieService,
    private translate: TranslateService
  ) {
  }

  setDefaultTargetAvatar(event: Event) {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.src = '/assets/acceuil/Icons/Profil%20Avatar.jpg';
    }
  }

  onStudentSelected(student: Student) {
    this.selectedStudent = student;
    this.selectedConversation = null;
    this.currentConversation = null;
    this.groupedMessages = []; // Clear grouped messages on student change
    if (student) {
      this.loadConversations(student.idUserStudent);
    }
  }

  loadConversations(studentId: number) {
    setTimeout(() => {
      this.isLoadingConversations = true;
    });
    this.conversations = [];
    this.selectedConversation = null;
    this.currentConversation = null;
    this.groupedMessages = [];
    this.currentConversationId = 0;
    this.currentFriendId = 0;

    this.messagerieService.getConversations(studentId).subscribe({
      next: (conversations) => {
        this.conversations = conversations;
        setTimeout(() => {
          this.isLoadingConversations = false;
        });
      },
      error: (error) => {
        this.conversations = [];
        setTimeout(() => {
          this.isLoadingConversations = false;
        });
        Swal.fire({
          title: 'Erreur',
          text: 'Erreur lors du chargement des conversations. Veuillez réessayer.',
          icon: 'error'
        });
      }
    });
  }

  selectConversation(conversation: Conversation) {
    if (this.selectedConversation?.id === conversation.id) {
      return; // Already selected
    }
    
    this.selectedConversation = conversation;
    this.currentConversationId = conversation.id;
    this.currentFriendId = conversation.idFriend;
    this.groupedMessages = []; // Clear grouped messages on conversation change
    this.loadConversationMessages(conversation.id);
    
    // Mark conversation as seen
    this.markAsSeen(conversation);
  }

  // Mark conversation as seen
  markAsSeen(conversation: Conversation) {
    if (!this.selectedStudent || !conversation.id) {
      return;
    }
    
    this.messagerieService.markConversationAsSeen(this.selectedStudent.idUserStudent, conversation.id)
      .subscribe({
        next: () => {
          // Update local conversation to mark as seen
          conversation.unreadCount = 0;
          // Update the conversation in the list
          const index = this.conversations.findIndex(c => c.id === conversation.id);
          if (index !== -1) {
            this.conversations[index].unreadCount = 0;
          }
        },
        error: (error) => {
        }
      });
  }

  private loadConversationMessages(conversationId: number) {
    if (!this.selectedStudent) {
      return;
    }
    
    this.isLoadingConversation = true;
    this.isLoadingMessages = true; // Set loading to true when messages start loading
    this.currentConversation = null;
    this.groupedMessages = []; // Clear grouped messages before loading new ones
    
    // Stop any existing polling
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
      this.isPollingActive = false;
    }

    this.messagerieService.getMessagesByConversation(conversationId).subscribe({
      next: (messages) => {
        const mappedMessages = messages.map(message => ({
          active: message.active || 0,
          any: message.any || [],
          anyNs: message.anyNs || [],
          conversation: {
            id: message.conversation?.id || 0,
            idUsersConverstion: message.conversation?.idUsersConverstion || '',
            idUser1: message.conversation?.idUser1 || 0,
            idUser2: message.conversation?.idUser2 || 0,
            nameUser1: message.conversation?.nameUser1 || '',
            nameUser2: message.conversation?.nameUser2 || ''
          },
          creationDate: message.creationDate ? new Date(message.creationDate) : new Date(),
          id: message.id || 0,
          listPieceJoint: message.listPieceJoint || { pieceJoint: [] },
          messageContent: message.messageContent || '',
          user: {
            id: message.user?.id || 0,
            firstname: message.user?.firstname || '',
            lastname: message.user?.lastname || ''
          },
          withPieceJoint: message.withPieceJoint || 0
        }));
        
        
        // Group messages by date
        this.groupedMessages = this.groupMessagesByDate(mappedMessages);

        // Update both selectedConversation and currentConversation with messages
        if (this.selectedConversation) {
          this.selectedConversation.messages = mappedMessages;
        }
        
        this.currentConversation = {
          id: conversationId,
          idFriend: this.currentFriendId,
          friendName: this.selectedConversation?.friendName || '',
          messages: mappedMessages,
          unreadCount: 0,
          avatar: this.selectedConversation?.avatar
        };
        
        this.isLoadingConversation = false;
        this.isLoadingMessages = false; // Set loading to false after messages are loaded
        setTimeout(() => this.scrollToBottom(), 100);
      },
      error: (error) => {
        this.currentConversation = null;
        this.isLoadingConversation = false;
        this.isLoadingMessages = false; // Set loading to false on error
        Swal.fire({
          title: 'Erreur',
          text: 'Erreur lors du chargement des messages. Veuillez réessayer.',
          icon: 'error'
        });
      }
    });
  }

  sendMessage() {
    if (!this.newMessage.trim() && this.selectedFiles.length === 0) {
      return;
    }
    
    if (!this.selectedStudent || this.isSendingMessage) {
      return;
    }
    
    if (this.currentConversationId === 0 && this.currentFriendId === 0) {
      Swal.fire({
        title: 'Erreur',
        text: 'Veuillez sélectionner une conversation ou un destinataire.',
        icon: 'warning'
      });
      return;
    }
    
    this.isSendingMessage = true;
    const messageContent = this.newMessage.trim();
    
    const messageRequest: AddMessageRequest = {
      idConversation: this.currentConversationId || 0,
      idUserStudent: this.selectedStudent.idUserStudent,
      idUserFriend: this.currentFriendId,
      contentMessage: messageContent,
      idOrganization: this.selectedStudent.idOrg
    };
    
    // Check if it's a new conversation
    if (this.currentConversationId === 0) {
      // This is a new conversation, so we need to create it first
      // For now, we'll just send the message. The backend should handle conversation creation.
      // In a real application, you might have a separate API call to create a conversation.
    }

    const initialConversationId = this.currentConversationId; // Store initial value

    this.messagerieService.sendMessage(messageRequest, this.selectedFiles).subscribe({
      next: (response) => {
        this.newMessage = '';
        this.selectedFiles = [];
        this.isSendingMessage = false;

        // After sending a message, refresh the current conversation messages
        if (initialConversationId !== 0) { // If it was an existing conversation
          this.loadConversationMessages(this.currentConversationId);
        } else { // If it was a new conversation (initialConversationId was 0)
          if (response && response.idConversation) {
            // If a new conversation was created and ID is returned
            this.currentConversationId = response.idConversation;
            this.loadConversationMessages(this.currentConversationId);
          } else {
            // Fallback: if no idConversation in response, try to get it via getMessagesByFriend
            console.warn('MessagerieComponent: No idConversation in sendMessage response for new conversation. Attempting to retrieve via getMessagesByFriend.');
            this.messagerieService.getMessagesByFriend(this.selectedStudent!.idUserStudent, this.currentFriendId).subscribe({
              next: (messages) => {
                if (messages && messages.length > 0) {
                  this.currentConversationId = messages[0].conversation.id;
                  this.loadConversationMessages(this.currentConversationId);
                } else {
                  console.error('MessagerieComponent: Could not retrieve new conversation ID after sending message.');
                  // Optionally, reload conversations to allow user to manually select
                  if (this.selectedStudent) {
                    this.loadConversations(this.selectedStudent.idUserStudent);
                  }
                }
              },
              error: (err) => {
                console.error('MessagerieComponent: Error retrieving messages by friend after sending new message:', err);
                if (this.selectedStudent) {
                  this.loadConversations(this.selectedStudent.idUserStudent);
                }
              }
            });
          }
        }
      },
      error: (error) => {
        console.error('MessagerieComponent: Error sending message:', error);
        this.isSendingMessage = false;
        Swal.fire({
          title: 'Erreur',
          text: 'Erreur lors de l\'envoi du message. Veuillez réessayer.',
          icon: 'error'
        });
      }
    });
  }

  onKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  attachFile() {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.multiple = true;
    fileInput.onchange = (event) => {
      const files = (event.target as HTMLInputElement).files;
      if (files) {
        for (let i = 0; i < files.length; i++) {
          this.selectedFiles.push(files[i]);
        }
      }
    };
    fileInput.click();
  }

  removeFile(index: number) {
    this.selectedFiles.splice(index, 1);
  }

  clearFiles() {
    this.selectedFiles = [];
  }

  scrollToBottom(): void {
    try {
      if (this.messagesContainerRef) {
        this.messagesContainerRef.nativeElement.scrollTop = this.messagesContainerRef.nativeElement.scrollHeight;
      }
    } catch (err) {
      console.error('Could not scroll to bottom:', err);
    }
  }

  // New method to group messages by date
  private groupMessagesByDate(messages: any[]): GroupedMessages[] {
    const groups: { [key: string]: any[] } = {};
    messages.forEach(message => {
      const messageDate = new Date(message.creationDate);
      const dateKey = messageDate.toDateString(); // e.g., "Mon Oct 26 2023"
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(message);
    });

    return Object.keys(groups).map(dateKey => ({
      date: this.formatDateForDisplay(new Date(dateKey)),
      messages: groups[dateKey].reverse() // Reverse the messages here
    })).sort((a, b) => {
      const dateA = a.date;
      const dateB = b.date;

      if (dateA === 'Today') return 1;
      if (dateB === 'Today') return -1;
      if (dateA === 'Yesterday') return 1;
      if (dateB === 'Yesterday') return -1;

      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });
  }

  // New method to format date for display (e.g., Today, Yesterday, Mon Oct 26, 2023)
  formatDateForDisplay(date: Date): string {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (messageDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (messageDate.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    } else if (now.getTime() - date.getTime() < 7 * 24 * 60 * 60 * 1000) {
      // Last 7 days: EEE (e.g., Mon)
      return date.toLocaleDateString([], { weekday: 'short' });
    } else if (date.getFullYear() === now.getFullYear()) {
      // This year: MMM d (e.g., Oct 26)
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } else {
      // Older: MMM d, y (e.g., Oct 26, 2023)
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    }
  }

  formatMessageTime(date: Date): string {
    if (!date) {
      return '';
    }
    // Check if date is a valid Date object
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      // Attempt to parse if it's a string, otherwise return empty
      const parsedDate = new Date(date);
      if (!(parsedDate instanceof Date) || isNaN(parsedDate.getTime())) {
        return '';
      }
      date = parsedDate;
    }

    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  // Open modal to select message targets
  openTargetsModal() {
    if (!this.selectedStudent) {
      Swal.fire({
        title: 'Attention',
        text: 'Veuillez sélectionner un étudiant.',
        icon: 'warning'
      });
      return;
    }
    
    this.showTargetsModal = true;
    this.loadMessageTargets();
  }
  
  // Close targets modal
  closeTargetsModal() {
    this.showTargetsModal = false;
    this.messageTargets = [];
  }
  
  // Load available message targets
  private loadMessageTargets() {
    if (!this.selectedStudent) {
      return;
    }
    
    this.isLoadingTargets = true;
    this.messageTargets = [];
    
    this.messagerieService.getMessageTargets(this.selectedStudent.idOrg).subscribe({
      next: (targets) => {
        this.messageTargets = targets;
        this.isLoadingTargets = false;
      },
      error: (error) => {
        this.messageTargets = [];
        this.isLoadingTargets = false;
        Swal.fire({
          title: 'Erreur',
          text: 'Erreur lors du chargement des destinataires. Veuillez réessayer.',
          icon: 'error'
        });
      }
    });
  }
  
  // Select a target from the modal
  selectTarget(target: MessageTarget) {
    if (!this.selectedStudent) {
      return;
    }
    
    // Check if conversation already exists
    const existingConversation = this.conversations.find(conv => 
      conv.idFriend === target.id
    );
    
    if (existingConversation) {
      // Select existing conversation
      this.selectConversation(existingConversation);
    } else {
      // Create new conversation object for UI
      const newConversation: Conversation = {
        id: 0, // Will be set when first message is sent
        idFriend: target.id,
        friendName: target.name,
        messages: [],
        unreadCount: 0,
        avatar: target.avatar
      };
      
      // Add to conversations list and select it
      this.conversations.unshift(newConversation);
      this.selectConversation(newConversation);
    }
    
    this.closeTargetsModal();
  }

  logFileLink(url: string) {
  }

  openFileModal(file: any) {
    const url = file.piece;
    const parts = url.split('.');
    const extension = parts.length > 1 ? parts.pop()?.toLowerCase() : '';

    let fileType = '';
    if (extension === 'png' || extension === 'jpg' || extension === 'jpeg' || extension === 'gif' || extension === 'jfif') {
      fileType = 'image/' + extension;
    } else if (extension === 'pdf') {
      fileType = 'application/pdf';
    }

    file.type = fileType;
    this.currentFile = file;
    this.showFileModal = true;
  }

  closeFileModal() {
    this.showFileModal = false;
    this.currentFile = null;
  }

  isImage(fileType: string | undefined): boolean {
    return !!(fileType && fileType.startsWith('image'));
  }

  isPdf(fileType: string | undefined): boolean {
    return !!(fileType && fileType === 'application/pdf');
  }
}

